import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, ArrowUp, ArrowDown, Search } from 'lucide-react';

export function ManagerReportsTab({ ctx }) {
  const { weeklyReport, selectedWeek, branches } = ctx;

  const [branchFilter, setBranchFilter] = useState('all');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [sortCol, setSortCol] = useState('total_hours');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowDown className="w-3 h-3 opacity-30 ml-1 inline" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-white" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-white" />;
  };

  const filtered = useMemo(() => {
    if (!weeklyReport?.staff_hours) return [];
    let list = weeklyReport.staff_hours;
    if (branchFilter !== 'all') {
      list = list.filter(s => s.branches?.includes(branchFilter));
    }
    if (employeeSearch.trim()) {
      const q = employeeSearch.toLowerCase();
      list = list.filter(s => s.username?.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortCol === 'username') { av = a.username?.toLowerCase() || ''; bv = b.username?.toLowerCase() || ''; }
      else if (sortCol === 'days_scheduled') { av = a.days_scheduled || 0; bv = b.days_scheduled || 0; }
      else { av = a.total_hours || 0; bv = b.total_hours || 0; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [weeklyReport, branchFilter, employeeSearch, sortCol, sortDir]);

  return (
    <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-lg font-bold text-stone-800">Weekly Hours Report</h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Week of {new Date(selectedWeek).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {branches && branches.length > 1 && (
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="w-44" data-testid="report-branch-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search employee..."
            value={employeeSearch}
            onChange={e => setEmployeeSearch(e.target.value)}
            className="pl-9"
            data-testid="report-employee-search"
          />
        </div>
        {(branchFilter !== 'all' || employeeSearch) && (
          <button onClick={() => { setBranchFilter('all'); setEmployeeSearch(''); }} className="text-xs text-stone-500 hover:text-stone-700 underline" data-testid="report-clear-filters">
            Clear filters
          </button>
        )}
        <span className="text-xs text-stone-500 ml-auto">{filtered.length} staff shown</span>
      </div>

      {filtered.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="report-table">
            <thead>
              <tr className="bg-gradient-to-r from-gray-600 to-gray-700 text-white">
                <th className="px-4 py-3 text-left font-semibold cursor-pointer select-none" onClick={() => toggleSort('username')} data-testid="sort-staff-member">
                  Staff Member <SortIcon col="username" />
                </th>
                <th className="px-4 py-3 text-center font-semibold">Branches</th>
                <th className="px-4 py-3 text-center font-semibold cursor-pointer select-none" onClick={() => toggleSort('days_scheduled')} data-testid="sort-days-scheduled">
                  Days Scheduled <SortIcon col="days_scheduled" />
                </th>
                <th className="px-4 py-3 text-right font-semibold cursor-pointer select-none" onClick={() => toggleSort('total_hours')} data-testid="sort-total-hours">
                  Total Hours <SortIcon col="total_hours" />
                </th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((staff, index) => (
                <tr key={staff.user_id} data-testid={`staff-hours-${staff.username}`}
                  className={`border-b border-stone-200 hover:bg-gray-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-stone-800">{staff.username}</div></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {staff.branches?.map(branch => (
                        <span key={branch} className="px-2 py-0.5 bg-stone-200 text-stone-700 text-xs rounded">{branch}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-stone-600">{staff.days_scheduled || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-xl font-bold text-stone-800">{staff.total_hours.toFixed(1)}</span>
                    <span className="text-sm text-stone-600 ml-1">hrs</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {staff.total_hours > 45
                      ? <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full">OVERTIME</span>
                      : <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">Regular</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-stone-100 font-bold">
                <td className="px-4 py-3 text-stone-800">Total ({filtered.length})</td>
                <td className="px-4 py-3 text-center text-stone-800"></td>
                <td className="px-4 py-3 text-center text-stone-800">{filtered.reduce((s, st) => s + (st.days_scheduled || 0), 0)}</td>
                <td className="px-4 py-3 text-right text-stone-800">{filtered.reduce((s, st) => s + st.total_hours, 0).toFixed(1)} hrs</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-stone-500">
          <BarChart className="w-12 h-12 mx-auto mb-3 text-stone-400" />
          <p>{weeklyReport?.staff_hours?.length > 0 ? 'No results match your filters' : 'No shifts scheduled for this week'}</p>
        </div>
      )}
    </div>
  );
}
